import React, { useState } from "react";
import { Star, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";

export const BatchBadge = ({ batch, testId }) => (
  <span
    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono font-bold border"
    style={batch.starred
      ? { backgroundColor: "#fef3c7", color: "#92400e", borderColor: "#f59e0b" }
      : { backgroundColor: "white", color: "var(--hi-olive)", borderColor: "#cbd5e1" }}
    data-testid={testId}
  >
    {batch.starred && <Star className="h-3 w-3 fill-amber-500 text-amber-500" />}
    #{batch.number}
  </span>
);

const SaveBatchDialog = ({ open, onOpenChange, postcodes, onSaved }) => {
  const [name, setName] = useState("");
  const [starred, setStarred] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/map-batches", { name: name.trim(), postcodes, starred });
      toast.success(`Saved as Batch #${data.number}`, { description: `${data.postcodes.length} postcodes · ${data.property_count} properties` });
      setName(""); setStarred(false);
      onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not save batch", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none" data-testid="save-batch-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Save {postcodes.length} postcodes as a batch</DialogTitle>
        </DialogHeader>
        <Input
          autoFocus
          placeholder="e.g. Diss north — week 1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          className="rounded-none"
          data-testid="save-batch-name-input"
        />
        <button
          type="button"
          onClick={() => setStarred((s) => !s)}
          className="flex items-center gap-2 text-sm"
          data-testid="save-batch-star-toggle"
        >
          <Star className={`h-4 w-4 ${starred ? "fill-amber-500 text-amber-500" : "text-slate-400"}`} />
          {starred ? "Starred (pinned to top)" : "Star this batch"}
        </button>
        <DialogFooter>
          <Button onClick={save} disabled={saving || !name.trim()} className="rounded-none text-white" style={{ backgroundColor: "var(--hi-olive)" }} data-testid="save-batch-confirm">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save batch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BatchRow = ({ batch, active, canEdit, onLoad, onChanged }) => {
  const toggleStar = async (e) => {
    e.stopPropagation();
    const { data } = await api.patch(`/map-batches/${batch.id}`, { starred: !batch.starred });
    onChanged(data);
  };
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete Batch #${batch.number} "${batch.name}"?`)) return;
    await api.delete(`/map-batches/${batch.id}`);
    toast.success(`Batch #${batch.number} deleted`);
    onChanged(null, batch.id);
  };
  return (
    <div
      role="button"
      onClick={() => onLoad(batch)}
      className="flex items-center gap-2 px-2.5 py-1.5 border-2 text-[11px] cursor-pointer transition-colors"
      style={active ? { borderColor: "var(--hi-olive)", backgroundColor: "var(--hi-cream)" } : { borderColor: "#e2e8f0", backgroundColor: "white" }}
      data-testid={`map-batch-${batch.number}`}
      title={batch.postcodes.join(", ")}
    >
      <BatchBadge batch={batch} testId={`map-batch-badge-${batch.number}`} />
      <span className="font-bold">{batch.name}</span>
      <span className="font-mono opacity-70">{batch.postcodes.length}pc · ×{batch.property_count}</span>
      {canEdit && (
        <>
          <button type="button" onClick={toggleStar} title={batch.starred ? "Unstar" : "Star"} data-testid={`map-batch-star-${batch.number}`}>
            <Star className={`h-3.5 w-3.5 ${batch.starred ? "fill-amber-500 text-amber-500" : "text-slate-400 hover:text-amber-500"}`} />
          </button>
          <button type="button" onClick={remove} title="Delete batch" data-testid={`map-batch-delete-${batch.number}`}>
            <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-600" />
          </button>
        </>
      )}
    </div>
  );
};

export default function SavedMapBatches({ batches, setBatches, activeBatchId, focusedPostcodes, canEdit, onLoad }) {
  const [saveOpen, setSaveOpen] = useState(false);
  const sortBatches = (list) => [...list].sort((a, b) => (b.starred - a.starred) || (a.number - b.number));

  const onChanged = (updated, deletedId) => {
    setBatches((prev) => sortBatches(deletedId ? prev.filter((b) => b.id !== deletedId) : prev.map((b) => (b.id === updated.id ? updated : b))));
  };

  return (
    <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap gap-2 items-center" data-testid="saved-map-batches">
      <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mr-1">Saved batches</span>
      {batches.length === 0 && (
        <span className="text-[11px] text-slate-500" data-testid="saved-map-batches-empty">None yet — select postcodes below, then save them as a numbered batch.</span>
      )}
      {batches.map((b) => (
        <BatchRow key={b.id} batch={b} active={b.id === activeBatchId} canEdit={canEdit} onLoad={onLoad} onChanged={onChanged} />
      ))}
      {canEdit && focusedPostcodes.size > 0 && (
        <Button size="sm" variant="outline" onClick={() => setSaveOpen(true)} className="rounded-none h-7 text-[11px] border-slate-300 ml-auto" data-testid="save-selection-as-batch-btn">
          <Save className="h-3.5 w-3.5 mr-1" /> Save selection as batch
        </Button>
      )}
      <SaveBatchDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        postcodes={Array.from(focusedPostcodes)}
        onSaved={(b) => { setBatches((prev) => sortBatches([...prev, b])); onLoad(b); }}
      />
    </div>
  );
}
